/**
 * Header-identity gate for draft edits (kanban 3019f212).
 *
 * Before the order-detail modal saves a new customer_no / po_number on an
 * agent-originated draft, it asks the API whether the value LEAVES the PO
 * the draft was generated from. A warning turns into
 * `HeaderIdentityWarningModal`; no warning saves straight through.
 *
 * Fail-open by design: a hand-keyed order (no extracted identity), a draft
 * the CSR already ruled on, or a failed check all resolve to `null` so the
 * edit proceeds exactly as before this gate existed.
 */
import { portalOrderService } from '../../services/portalOrders';
import type { HeaderIdentityCheckResponse } from '../../services/portalOrders';
import type { OrderRecord } from '../../types/orders';

export interface HeaderIdentityRuling {
  /** True when the CSR answered "yes, different order". */
  differentOrder: boolean;
  note?: string | null;
}

export interface PendingHeaderIdentityWarning<T> {
  check: HeaderIdentityCheckResponse;
  /** The edit that was held back until the CSR answers. */
  pending: T;
}

export interface HeaderIdentityPatch {
  customer_no?: string;
  po_number?: string;
}

/** Does this draft carry an extraction origin the edit could leave? */
export function hasExtractedIdentity(order: OrderRecord): boolean {
  if (order.source_identity_detached_at) return false;
  return Boolean(order.extracted_customer_no || order.extracted_po_number);
}

/**
 * Resolve to the warning to show, or `null` to save without asking.
 * Never throws.
 */
export async function runHeaderIdentityCheck(
  order: OrderRecord,
  patch: HeaderIdentityPatch,
): Promise<HeaderIdentityCheckResponse | null> {
  if (!hasExtractedIdentity(order)) return null;
  if (patch.customer_no === undefined && patch.po_number === undefined) return null;
  try {
    const result = await portalOrderService.checkHeaderIdentity(order.id, patch);
    return result.warn ? result : null;
  } catch (err) {
    console.error('Header identity check failed; saving without the warning', err);
    return null;
  }
}
